import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageSquare,
  Hash,
  Bell,
  Send,
  RefreshCw,
  Search,
  Plus,
  Settings,
  ArrowLeft,
  Users,
  AtSign,
  CheckCircle,
  AlertCircle,
  ExternalLink,
  Volume2,
  VolumeX,
  Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;

// Notification types
const NOTIFICATION_TYPES = [
  { id: 'new_application', name: 'New Application', description: 'When a candidate applies for a job', default: true },
  { id: 'interview_scheduled', name: 'Interview Scheduled', description: 'When an interview is scheduled', default: true },
  { id: 'candidate_status', name: 'Candidate Status Change', description: 'When candidate status is updated', default: true },
  { id: 'offer_sent', name: 'Offer Sent', description: 'When an offer letter is sent', default: true },
  { id: 'offer_accepted', name: 'Offer Accepted', description: 'When candidate accepts offer', default: true },
  { id: 'daily_summary', name: 'Daily Summary', description: 'Daily recruitment summary', default: false },
];

const SlackIntegration = ({ onBack }) => {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [notifications, setNotifications] = useState({});
  const [showSettings, setShowSettings] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [workspace, setWorkspace] = useState(null);

  useEffect(() => {
    checkConnection();
  }, []);

  useEffect(() => {
    if (connected && selectedChannel) {
      fetchMessages();
    }
  }, [selectedChannel]);

  const checkConnection = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/slack/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConnected(response.data.connected);
      setWorkspace(response.data.workspace);
      setNotifications(response.data.notifications || {});
      if (response.data.connected) {
        await fetchChannels();
      }
    } catch (error) {
      console.error('Error checking Slack connection:', error);
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const fetchChannels = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/slack/channels`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChannels(response.data.channels || []);
    } catch (error) {
      // Demo channels
      setChannels([
        { id: 'ch1', name: 'recruitment', is_private: false, num_members: 12 },
        { id: 'ch2', name: 'hr-announcements', is_private: false, num_members: 25 },
        { id: 'ch3', name: 'interviews', is_private: true, num_members: 5 },
        { id: 'ch4', name: 'general', is_private: false, num_members: 50 },
      ]);
    }
  };

  const fetchMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/integrations/slack/messages/${selectedChannel.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      // Demo messages
      setMessages([
        {
          id: 'm1',
          user: { name: 'HR Bot', avatar: '🤖' },
          text: '📢 New application received for Software Engineer position',
          ts: new Date(Date.now() - 300000).toISOString(),
          reactions: [{ name: '👍', count: 3 }]
        },
        {
          id: 'm2',
          user: { name: 'Sarah', avatar: '👩' },
          text: 'Interview scheduled with John Doe for tomorrow at 10 AM',
          ts: new Date(Date.now() - 600000).toISOString()
        },
        {
          id: 'm3',
          user: { name: 'Mike', avatar: '👨' },
          text: 'Great candidate! Moving forward with the offer.',
          ts: new Date(Date.now() - 900000).toISOString(),
          reactions: [{ name: '🎉', count: 5 }]
        }
      ]);
    }
  };

  const handleConnect = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/api/integrations/slack/connect`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.oauth_url) {
        window.location.href = response.data.oauth_url;
      } else {
        toast.info(response.data.message || 'Slack connection initiated');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to connect Slack');
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !selectedChannel) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/integrations/slack/send`, {
        channel_id: selectedChannel.id,
        text: messageText
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setMessageText('');
      toast.success('Message sent');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleUpdateNotifications = async (notifId, enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/integrations/slack/notifications`, {
        [notifId]: enabled
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setNotifications(prev => ({ ...prev, [notifId]: enabled }));
      toast.success('Notification settings updated');
    } catch (error) {
      toast.error('Failed to update settings');
    }
  };

  const formatTime = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="h-8 w-8 animate-spin text-purple-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="p-6">
        <Button variant="ghost" onClick={onBack} className="mb-4">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to App Hub
        </Button>
        
        <Card className="max-w-lg mx-auto">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-8 w-8 text-purple-600" />
            </div>
            <h2 className="text-xl font-bold text-slate-800 mb-2">Connect Slack</h2>
            <p className="text-slate-500 mb-6">
              Get real-time recruitment notifications in your Slack workspace and send messages directly from Officium Connect.
            </p>
            <Button onClick={handleConnect} className="bg-purple-600 hover:bg-purple-700">
              <MessageSquare className="h-4 w-4 mr-2" />
              Add to Slack
            </Button>
            <div className="mt-6 text-left">
              <h4 className="font-medium text-slate-700 mb-3">Notifications you will receive:</h4>
              <ul className="space-y-2 text-sm text-slate-600">
                {NOTIFICATION_TYPES.filter(n => n.default).map(notif => (
                  <li key={notif.id} className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-500" />
                    {notif.name}
                  </li>
                ))}
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" data-testid="slack-integration">
      {/* Header */}
      <div className="p-4 border-b bg-white flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center">
            <MessageSquare className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="font-semibold text-slate-800">Slack</h1>
            <p className="text-xs text-slate-500">{workspace?.name || 'Connected'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setShowSettings(true)}>
            <Settings className="h-4 w-4 mr-2" />
            Notifications
          </Button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Channel List */}
        <div className="w-64 border-r bg-slate-50 p-3">
          <h3 className="text-xs font-semibold uppercase text-slate-500 mb-3 px-2">Channels</h3>
          <div className="space-y-1">
            {channels.map(channel => (
              <button
                key={channel.id}
                onClick={() => setSelectedChannel(channel)}
                className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                  selectedChannel?.id === channel.id 
                    ? 'bg-purple-100 text-purple-700' 
                    : 'text-slate-600 hover:bg-white'
                }`}
              >
                {channel.is_private ? (
                  <span className="text-slate-400">🔒</span>
                ) : (
                  <Hash className="h-4 w-4 text-slate-400" />
                )}
                <span className="truncate">{channel.name}</span>
                <span className="ml-auto text-xs text-slate-400">{channel.num_members}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 flex flex-col">
          {selectedChannel ? (
            <>
              {/* Channel Header */}
              <div className="p-4 border-b">
                <div className="flex items-center gap-2">
                  <Hash className="h-5 w-5 text-slate-400" />
                  <h2 className="font-semibold text-slate-800">{selectedChannel.name}</h2>
                  <Badge variant="outline" className="ml-2">
                    <Users className="h-3 w-3 mr-1" />
                    {selectedChannel.num_members}
                  </Badge>
                </div>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {messages.map(msg => (
                  <div key={msg.id} className="flex gap-3">
                    <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-slate-200 to-slate-300 flex items-center justify-center text-lg">
                      {msg.user.avatar}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-baseline gap-2 mb-1">
                        <span className="font-medium text-slate-800">{msg.user.name}</span>
                        <span className="text-xs text-slate-500">{formatTime(msg.ts)}</span>
                      </div>
                      <p className="text-slate-700">{msg.text}</p>
                      {msg.reactions && (
                        <div className="flex gap-1 mt-2">
                          {msg.reactions.map((reaction, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-slate-100 rounded-full text-xs">
                              {reaction.name} {reaction.count}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Message Input */}
              <div className="p-4 border-t">
                <div className="flex gap-2">
                  <Input
                    placeholder={`Message #${selectedChannel.name}`}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleSendMessage()}
                  />
                  <Button onClick={handleSendMessage} disabled={sending || !messageText.trim()}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-slate-500">
              <div className="text-center">
                <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>Select a channel to view messages</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Notification Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Settings
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-500">
              Choose which recruitment events should send notifications to Slack.
            </p>
            {NOTIFICATION_TYPES.map(notif => (
              <div key={notif.id} className="flex items-center justify-between py-2">
                <div>
                  <p className="font-medium text-slate-700">{notif.name}</p>
                  <p className="text-xs text-slate-500">{notif.description}</p>
                </div>
                <Switch
                  checked={notifications[notif.id] ?? notif.default}
                  onCheckedChange={(checked) => handleUpdateNotifications(notif.id, checked)}
                />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => setShowSettings(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SlackIntegration;
